import {isRight} from 'fp-ts/lib/Either'
import reporter from 'io-ts-reporters'
import React, {useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {
  StreamFilterArrayCodec,
  StreamFilterCompareOperatorCodec
} from '../../../common/types'
import {Error} from '../../../components/auth/layout'
import {PreviewFlagMarker} from '../../../components/feature-flags'
import {FormRow} from '../../../components/form'
import {JSONField} from '../../../components/library/json-field'
import {Button} from '../../../components/library/buttons'
import {Copyable} from '../../../components/library/copy'
import {
  DropdownBody,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {Dropdown} from '../../../components/library/dropdown/dropdown'
import {InputGroup, Select, TextInput} from '../../../components/library/form'
import {CollapsibleSection} from '../../../components/library/form/collapsible-section'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResultsPermissions
} from '../../../components/library/list'
import {Modal} from '../../../components/library/modal/modal'
import {Tag} from '../../../components/library/tag/tag'
import {Switch} from '../../../components/library/toggles/switch'
import {Tooltip} from '../../../components/library/tooltip/tooltip'
import {Loader} from '../../../components/loader'
import {
  MagicLinkComposer,
  MagicLinkModalTypes
} from '../../../components/magic-link-composer'
import {LegacyFormModal} from '../../../components/modals'
import {
  StreamDetailFragment,
  useStreamListLazyQuery
} from '../../../data/workbench-api/generated/stream'
import {
  StreamContainerDetailFragment,
  useStreamContainerListQuery
} from '../../../data/workbench-api/generated/stream-container'
import {
  ThemeDetailFragment,
  useThemeListQuery
} from '../../../data/workbench-api/generated/theme'
import {
  PermissionAction,
  PermissionResource,
  StreamsOrderBy
} from '../../../data/workbench-api/generated/types'
import {
  ADD_STREAM_CONTAINER,
  EDIT_STREAM_CONTAINER
} from '../../../data/workbench-api/stream-container'
import {EnvironmentNode, useEnvironment} from '../../../environment'
import {useFlags} from '../../../hooks/use-flags'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {
  IconHeading,
  Mono,
  Section,
  SectionContent,
  SectionInfo
} from '../components'
import {Overflow} from './overflow'

const streamContainerIsValid = (
  obj: Partial<StreamContainerDetailFragment>,
  error: string
) => !!obj.streamId && !!obj.name && !!obj.themeId && !error

type CurrentModal =
  | {
      type: 'new'
      streamContainer: {
        environmentId: string
        name: string
        cardLimit: number
        filter: unknown[]
      }
    }
  | {
      type: 'new' | 'edit'
      streamContainer: StreamContainerDetailFragment
    }

interface NewStreamContainer {
  environmentId: string
  name: string
  cardLimit: number
  streamId?: string
  filter?: unknown[]
}

const StreamSelect = ({
  streamContainer,
  updateStreamContainer,
  streams
}: {
  streamContainer: Partial<StreamContainerDetailFragment>
  updateStreamContainer: (obj: Partial<StreamContainerDetailFragment>) => void
  streams: StreamDetailFragment[]
}) => (
  <Select
    label="Add a stream..."
    upLabel="Stream"
    value={streamContainer.streamId}
    options={streams.map(item => ({
      text: item.name,
      value: item.id
    }))}
    onChange={value => {
      updateStreamContainer({streamId: value})
    }}
  />
)

const ThemeSelect = ({
  streamContainer,
  updateStreamContainer,
  themes
}: {
  streamContainer: Partial<StreamContainerDetailFragment>
  updateStreamContainer: (obj: Partial<StreamContainerDetailFragment>) => void
  themes: ThemeDetailFragment[]
}) => {
  const [darkMode, setDarkMode] = useState<boolean>(
    !!streamContainer.darkThemeId
  )
  return (
    <>
      <InputGroup style={{marginTop: '20px'}}>
        <Select
          label="Assign a theme..."
          upLabel="Theme"
          value={streamContainer.themeId || undefined}
          options={themes
            .filter(item => item.id !== streamContainer.darkThemeId)
            .map(item => ({
              text: item.name as string,
              value: item.id
            }))}
          onChange={value => {
            updateStreamContainer({themeId: value})
          }}
        />

        <Switch
          label="Enable dark mode"
          isForm={true}
          labelPosition="left"
          checked={darkMode}
          onChange={enabled => {
            if (!enabled) {
              updateStreamContainer({darkThemeId: null})
            }
            setDarkMode(enabled)
          }}
        />
        {darkMode && (
          <Select
            label="Assign a dark theme..."
            upLabel="Dark theme"
            value={streamContainer.darkThemeId || undefined}
            options={themes
              .filter(item => item.id !== streamContainer.themeId)
              .map(item => ({
                text: item.name as string,
                value: item.id
              }))}
            onChange={value => {
              updateStreamContainer({darkThemeId: value})
            }}
          />
        )}
      </InputGroup>
    </>
  )
}

interface ContainersProps {
  environment: EnvironmentNode
}

export const StreamContainers = ({environment}: ContainersProps) => {
  const {
    data: containerData,
    refetch: refetchContainers,
    loading
  } = useStreamContainerListQuery({
    variables: {
      environmentId: environment.id
    }
  })
  const streamContainers = containerData?.streamContainers?.nodes

  const canEdit = environment.hasPermission(
    PermissionResource.StreamContainer,
    PermissionAction.Edit
  )
  const canView = environment.hasPermission(
    PermissionResource.StreamContainer,
    PermissionAction.View
  )

  const flags = useFlags(environment.organisationId)

  const [currentModal, setCurrentModal] = useState<CurrentModal | null>(null)
  const [error, setError] = useState<string>('')

  const [linkModal, setLinkModal] = useState<{
    streamContainer: StreamContainerDetailFragment
    context: MagicLinkModalTypes
  } | null>(null)

  const [query, {data: streamData}] = useStreamListLazyQuery({
    variables: {
      environmentId: environment.id,
      orderBy: [StreamsOrderBy.UpdatedDesc]
    }
  })

  useEffect(() => {
    if (canView) {
      query()
    }
  }, [canView, query])

  const streams = streamData?.streams?.nodes

  const {data: themeData, refetch: refetchThemes} = useThemeListQuery({
    variables: {
      environmentId: environment.id
    }
  })

  const themes = themeData?.themes?.nodes

  const streamContainerColumns = useMemo(
    (): ListColumns<StreamContainerDetailFragment> => [
      {
        id: 'name',
        header: 'Name',
        accessor: r => {
          const name = r.name || 'Untitled'
          return (
            <>
              {canEdit ? (
                <a
                  style={{marginRight: '-10px'}}
                  onClick={() =>
                    setCurrentModal({type: 'edit', streamContainer: r})
                  }
                >
                  {name}
                </a>
              ) : (
                <strong style={{marginRight: '10px'}}>{name}</strong>
              )}
              {r.enabled ? (
                <Tag appearance="green">Active</Tag>
              ) : (
                <Tag appearance="light">Inactive</Tag>
              )}
            </>
          )
        }
      },
      {
        id: 'id',
        header: 'ID',
        accessor: r => (
          <>
            <Mono style={{marginRight: '5px'}}>{r.id}</Mono>
            <Copyable style={{width: '70px'}} value={r.id} />
          </>
        )
      },
      {
        id: 'streamName',
        header: 'Stream',
        accessor: 'stream.name'
      },
      {
        id: 'themeName',
        header: 'Themes',
        accessor: r =>
          r.theme
            ? r.darkTheme
              ? r.theme.name + ', ' + r.darkTheme.name
              : r.theme.name
            : null
      },
      {
        id: 'cardLimit',
        header: 'Card limit',
        accessor: r => r.cardLimit
      },
      {
        id: 'magicLink',
        accessor: r => (
          <Button
            appearance="secondary"
            onClick={() =>
              setLinkModal({
                streamContainer: r,
                context: 'container'
              })
            }
            leftAlign={true}
          >
            Get magic auth link
          </Button>
        )
      },
      {
        id: 'isDefault',
        horizontalAlign: 'right',
        accessor: r =>
          r.isDefault ? <Tag appearance="light">Default</Tag> : null
      }
    ],
    [canEdit]
  )

  const env = useEnvironment()
  const currentTestUserId =
    env.organisation?.currentBusinessUser?.linkedTestUserId

  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed sdk stream containers configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>sdk>stream-containers'
      }
    })
  }, [analytics, environment])

  return (
    <Section>
      <Helmet>
        <title>Stream containers</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="container" />
          Stream containers
        </IconHeading>

        <nav>
          <Tooltip
            disabled={canEdit}
            content="Sorry, you don't have permission to perform this action"
            position="left"
          >
            <Button
              disabled={!canEdit}
              onClick={() =>
                setCurrentModal({
                  type: 'new',
                  streamContainer: {
                    environmentId: environment.id,
                    name: '',
                    streamId: undefined,
                    cardLimit: 50,
                    filter: []
                  }
                })
              }
            >
              New container
            </Button>
          </Tooltip>
        </nav>
      </header>

      <SectionInfo>
        <p>Create containers to display card streams in your apps</p>
      </SectionInfo>

      <SectionContent>
        <List<StreamContainerDetailFragment>
          columns={streamContainerColumns}
          rows={streamContainers ?? []}
          loading={loading}
          bleed={'#f2f2f2'}
          renderOverflow={(streamContainer, closeDropdown) => (
            <Overflow
              environment={environment}
              streamContainer={streamContainer}
              closeDropdown={closeDropdown}
              refetch={() => {
                refetchContainers()
                refetchThemes()
              }}
              edit={() => setCurrentModal({type: 'edit', streamContainer})}
              canEdit={canEdit}
            />
          )}
          renderEmpty={canView ? undefined : () => <NoResultsPermissions />}
        />

        {currentModal &&
          (currentModal.type === 'new' || currentModal.type === 'edit') && (
            <LegacyFormModal<NewStreamContainer>
              style={{minWidth: '600px'}}
              obj={currentModal.streamContainer}
              isValid={obj => streamContainerIsValid(obj, error)}
              handleClose={() => setCurrentModal(null)}
              title={
                currentModal.type === 'new'
                  ? 'Add new container'
                  : `Edit container`
              }
              submitText={currentModal.type === 'new' ? 'Add' : 'Save'}
              mutation={
                currentModal.type === 'new'
                  ? ADD_STREAM_CONTAINER
                  : EDIT_STREAM_CONTAINER
              }
              update={() => {
                analytics.track({
                  event:
                    currentModal.type === 'new'
                      ? 'Added configuration'
                      : 'Edited configuration',
                  properties: {
                    org_id: environment.organisationId,
                    environment_id: environment.id,
                    category: 'configuration>sdk>stream-containers'
                  }
                })

                refetchContainers()
                refetchThemes()
              }}
            >
              {(streamContainer, updateStreamContainer) => {
                return (
                  <>
                    <FormRow>
                      <TextInput
                        upLabel="Name"
                        label="Give it a name..."
                        value={streamContainer.name}
                        onChange={name => updateStreamContainer({name})}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInput
                        type="number"
                        upLabel="Card limit"
                        label="Give it a limit..."
                        value={String(streamContainer.cardLimit)}
                        onChange={cardLimit =>
                          updateStreamContainer({
                            cardLimit: parseInt(cardLimit)
                          })
                        }
                      />
                    </FormRow>

                    {streams ? (
                      <StreamSelect
                        streamContainer={streamContainer}
                        updateStreamContainer={updateStreamContainer}
                        streams={streams}
                      />
                    ) : (
                      <Loader variant="inline" />
                    )}
                    {themes ? (
                      <ThemeSelect
                        streamContainer={streamContainer}
                        updateStreamContainer={updateStreamContainer}
                        themes={themes}
                      />
                    ) : (
                      <Loader variant="inline" />
                    )}
                    {flags.isInternal ? (
                      <CollapsibleSection
                        title={
                          <span>
                            Advanced <PreviewFlagMarker />
                          </span>
                        }
                        defaultExpanded={
                          streamContainer.filter &&
                          streamContainer.filter.length > 0
                        }
                        style={{padding: '0px'}}
                      >
                        {error ? (
                          <Error style={{width: '540px'}}>{error}</Error>
                        ) : null}
                        <FormRow>
                          <JSONField
                            raw={true}
                            label="Filter"
                            value={streamContainer.filter}
                            onChange={filter => {
                              const decoded =
                                StreamFilterArrayCodec.decode(filter)

                              if (!isRight(decoded)) {
                                const reporterErrors = reporter.report(decoded)
                                console.error(reporterErrors)
                                setError(
                                  'Something is wrong with the filter, check that targetValue type makes sense for the chosen operator'
                                )
                                return
                              }

                              // Check for date parsability of comparison (lt, gt etc) operators
                              for (const f of decoded.right) {
                                if (
                                  StreamFilterCompareOperatorCodec.props.operator.is(
                                    f.operator
                                  ) &&
                                  typeof f.targetValue === 'string'
                                ) {
                                  const dateParsed = Date.parse(f.targetValue)
                                  if (!dateParsed) {
                                    setError(
                                      'When using comparison operators, targetValue must be parsable as a date'
                                    )
                                    return
                                  }
                                }
                              }

                              setError('')
                              return updateStreamContainer({
                                filter: decoded.right
                              })
                            }}
                            legacyMode={true}
                          />
                        </FormRow>
                        <Button
                          style={{marginRight: '10px'}}
                          onClick={() => {
                            updateStreamContainer({filter: []})
                            setError('')
                          }}
                          appearance="secondary"
                        >
                          Clear filter
                        </Button>
                        <Dropdown
                          trigger={(handleClick, ref, isOpen) => (
                            <Button
                              iconRight="arrow_down"
                              onClick={handleClick}
                              ref={ref}
                              appearance={isOpen ? 'tint' : 'secondary'}
                            >
                              Load an example filter
                            </Button>
                          )}
                        >
                          {closeDropdown => {
                            const exampleFilters = {
                              priority: [
                                {
                                  namespaceString: 'priority',
                                  operator: 'lt',
                                  targetValue: 6
                                }
                              ],
                              profile: [
                                {
                                  namespaceString:
                                    'endUserProfileSnapshot.timezone',
                                  operator: 'eq',
                                  targetValue: 'Pacific/Auckland'
                                }
                              ],
                              variable: [
                                {
                                  namespaceString:
                                    'definition.variables.favouritefood.default',
                                  operator: 'in',
                                  targetValue: ['Mushrooms', 'Bananas']
                                }
                              ],
                              eventPayload: [
                                {
                                  namespaceString:
                                    'eventPayload.detail.favouritefood',
                                  operator: 'ne',
                                  targetValue: 'Bananas'
                                }
                              ]
                            }
                            return (
                              <DropdownBody>
                                <DropdownItem>
                                  <a
                                    onClick={() => {
                                      updateStreamContainer({
                                        filter: exampleFilters.priority
                                      })
                                      closeDropdown()
                                    }}
                                  >
                                    Priority
                                  </a>
                                </DropdownItem>
                                <DropdownItem>
                                  <a
                                    onClick={() => {
                                      updateStreamContainer({
                                        filter: exampleFilters.profile
                                      })
                                      closeDropdown()
                                    }}
                                  >
                                    Profile field
                                  </a>
                                </DropdownItem>
                                <DropdownItem>
                                  <a
                                    onClick={() => {
                                      updateStreamContainer({
                                        filter: exampleFilters.variable
                                      })
                                      closeDropdown()
                                    }}
                                  >
                                    Variable
                                  </a>
                                </DropdownItem>
                                <DropdownItem>
                                  <a
                                    onClick={() => {
                                      updateStreamContainer({
                                        filter: exampleFilters.eventPayload
                                      })
                                      closeDropdown()
                                    }}
                                  >
                                    Variable payload
                                  </a>
                                </DropdownItem>
                              </DropdownBody>
                            )
                          }}
                        </Dropdown>
                      </CollapsibleSection>
                    ) : null}
                  </>
                )
              }}
            </LegacyFormModal>
          )}
        {linkModal && (
          <Modal
            handleClose={() => setLinkModal(null)}
            style={{maxWidth: '400px', padding: '0px'}}
            isFullScreen={false}
          >
            <MagicLinkComposer
              context={linkModal.context}
              organisationId={environment.organisationId}
              environmentId={environment.id}
              containerId={linkModal?.streamContainer?.id}
              defaultUserId={currentTestUserId}
              handleClose={() => setLinkModal(null)}
            />
          </Modal>
        )}
      </SectionContent>
    </Section>
  )
}
